import React, { useEffect, useRef, useState } from 'react';
import {
  Form,
  Input,
  List,
  Card,
  Button,
  Typography,
  Col,
  Row,
  Tag,
  Modal,
  Alert,
  Badge,
  Space,
} from 'antd';
import {
  CloseOutlined,
  FileUnknownOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  UsergroupAddOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getGames, createGame, addPlayer, addQuestion, deletePlayer, deleteQuestion, deleteGame } from '../../actions/games';
import { DeletePlayerProp, DeleteQuestionProp, GameProp, PlayerProp, QuestionProp } from '../../actions/types';
import classnames from 'classnames';

const { Title } = Typography;

interface GamesProps {
  currentGameId: string;
  isAddGameModalVisible: boolean;
  isAddPlayerModalVisible: boolean;
  isAddQuestionModalVisible: boolean;
  isDeleteGameModalVisible: boolean;
  isAddGameSubmitting: boolean;
  isDeleteGameSubmitting: boolean;
  isGetGameLoading: boolean;
}

interface DispatchProps {
  getGames: () => Promise<void>;
  createGame: (game: GameProp) => Promise<void>;
  deleteGame: (gameId: string) => Promise<void>;
  addPlayer: (player: PlayerProp, gameId: string) => Promise<void>;
  addQuestion: (question: QuestionProp, gameId: string) => Promise<void>;
  deletePlayer: (player: DeletePlayerProp, gameId: string) => Promise<void>;
  deleteQuestion: (question: DeleteQuestionProp, gameId: string) => Promise<void>;
}

interface StateProps {
  games: [GameProp];
}

const Games = ({ games, getGames, createGame, deleteGame, addPlayer, addQuestion, deletePlayer, deleteQuestion }: StateProps & DispatchProps) => { 
  const navigate = useNavigate();

  const [gamesState, setGamesState] = useState<GamesProps>({
    currentGameId: '',
    isAddGameModalVisible: false,
    isAddPlayerModalVisible: false,
    isAddQuestionModalVisible: false,
    isDeleteGameModalVisible: false,
    isAddGameSubmitting: false,
    isDeleteGameSubmitting: false,
    isGetGameLoading: false,
  });
  const [gameId, setGameId] = useState<string>('')

  const formRef = useRef<any | undefined>(null);
  
  useEffect(() => {
    setGamesState(prevState => ({
      ...prevState,
      isGetGameLoading: true,
    }));

    getGames().then(() =>  setGamesState(prevState => ({
      ...prevState,
      isGetGameLoading: false,
    })));
  }, []);

  // Game
  const handleOpenAddGameModal = () => {
    setGamesState(prevState => ({
      ...prevState,
      isAddGameModalVisible: !prevState.isAddGameModalVisible,
    }));
  };

  const handleOpenDeleteGameModal = () => {    
    setGamesState(prevState => ({
      ...prevState,
      isDeleteGameModalVisible: !prevState.isDeleteGameModalVisible,
    }));
  };

  const onAddGameFinish = (values: GameProp) => {
    setGamesState(prevState => ({
      ...prevState,
      isAddGameSubmitting: true,
    }));

    createGame(values);

    // @ts-ignore
    formRef.current.resetFields();

    setGamesState(prevState => ({
      ...prevState,
      isAddGameModalVisible: false,
      isAddGameSubmitting: false,
    }));
  };

  const onDeleteGameHandler = () => {
    setGamesState(prevState => ({
      ...prevState,
      isDeleteGameSubmitting: true,
    }));
    console.log('dd', gameId);
    
    deleteGame(gameId);

    setGamesState(prevState => ({
      ...prevState,
      isDeleteGameModalVisible: false,
      isDeleteGameSubmitting: false,
    }));
  }

  // Player
  const handleOpenAddPlayerModal = () => {
    setGamesState(prevState => ({
      ...prevState,
      isAddPlayerModalVisible: !prevState.isAddPlayerModalVisible,
    }));
  };

  const handleAddPlayer = (currentGame: GameProp) => {
    setGamesState(prevState => ({
      ...prevState,
      currentGameId: currentGame.gameId,
      isAddPlayerModalVisible: true,
    }));
  };

  const onPlayerFinish = (values: PlayerProp, gameId: string) => {    
    setGamesState(prevState => ({
      ...prevState,
      isAddGameSubmitting: true,
    }));

    addPlayer({ ...values },  gameId);

    // @ts-ignore
    formRef.current.resetFields();

    setGamesState(prevState => ({
      ...prevState,
      isAddGameSubmitting: false,
    }));
  };

  // Question
  const handleOpenAddQuestionModal = () => {
    setGamesState(prevState =>({
      ...prevState,
      isAddQuestionModalVisible: !prevState.isAddQuestionModalVisible,
    }));
  };

  const handleAddQuestion = (currentGame: GameProp) => {
    setGamesState(prevState => ({
      ...prevState,
      currentGameId: currentGame.gameId,
      isAddQuestionModalVisible: true,
    }));
  };

  const onQuestionFinish = (values: QuestionProp, gameId: string) => {
    setGamesState(prevState => ({
      ...prevState,
      isAddGameSubmitting: true,
    }));

    addQuestion({ ...values }, gameId);

    // @ts-ignore
    formRef.current.resetFields();

    setGamesState(prevState => ({
      ...prevState,
      isAddGameSubmitting: false,
    }));
  };

  const onPlayerFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const onQuestionFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const onAddGameFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const handleOnClick = (item: GameProp) => {
    navigate( `games/${item.gameId}`)
  };

  const { currentGameId, isAddGameModalVisible, isAddPlayerModalVisible, isDeleteGameModalVisible, isAddQuestionModalVisible, isAddGameSubmitting, isDeleteGameSubmitting, isGetGameLoading } = gamesState;
  
  const EmptyListAlert = () => (
    <Alert
      style={{ marginTop: 20, marginBottom: 20 }}
      title="At the moment there are no palyers to show"
      description="At the moment there are no palyers to show"
      type="info"
      showIcon={true}
    />
  );

  return (
    <>
      <Row justify="center" style={{ textAlign: 'center' }}>
        <Col xs={24} sm={24} md={24} lg={24} xl={20}>
          <Title level={4}>Welcome to the Family game! Create a game by pressing the "Create game" button bellow.</Title>
          <Button type="primary" size="large" onClick={handleOpenAddGameModal}>
            <PlusCircleOutlined />
            Create game
          </Button>
        </Col>
      </Row>
      <Row justify="center" style={{ marginTop: 40 }}>
        <Col xs={24} sm={24} md={24} lg={24} xl={20}>
          <List
            grid={{
              gutter: 48,
              xs: 1,
              sm: 1,
              md: 1,
              lg: 1,
              xl: 1,
              xxl: 1,
            }}
            loading={isGetGameLoading}
            locale={{ emptyText: <EmptyListAlert /> }}
            dataSource={games}
            renderItem={(item: GameProp) => (
              <List.Item>
                <Badge.Ribbon
                  className={classnames('seconds', {
                    'no-winner': Object.keys(item.winner).length == 0,
                  })}
                  text={Object.keys(item.winner).length > 0 ? 'Winner: ' + item.winner.name : ''}
                >
                  <Card title={item.title}>
                    {(item.players.length > item.questions.length && (
                      <Alert
                        title="Number of Players should not be more than Questions"
                        type="error"
                        showIcon={true}
                      />
                    )) ||
                      (item.players.length === 0 && item.questions.length === 0 && (
                        <Alert
                          title="Add questions and players to being playing the game"
                          type="error"
                          showIcon={true}
                        />
                      )) ||
                      (item.questions.length % item.players.length !== 0 && (
                        <Alert
                          title="Number of questions should be equally divisbile by number of players"
                          type="error"
                          showIcon={true}
                        />
                      ))}
                    <Row>
                      <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                        <div className="questions-section">
                          <div className="tags">
                            {item.questions.map((question) => (
                              <Tag
                                key={question?.questionId ?? question.question}
                                closable={Object.keys(item.winner).length === 0}
                                onClose={() =>
                                  deleteQuestion({ questionId: question.questionId }, item.gameId)
                                }
                              >
                                <div className="ant-tag-question">{question.question}</div>
                              </Tag>
                            ))}
                          </div>
                          <Button
                            type="primary"
                            size="small"
                            disabled={isAddGameSubmitting || Object.keys(item.winner).length > 0}
                            onClick={() => handleAddQuestion(item)}
                          >
                            <FileUnknownOutlined />
                            Add question
                          </Button>
                        </div>
                      </Col>
                      <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <div className="players-section">
                          <div className="tags">
                            {item.players.map((player) => (
                              <Tag
                                key={player?.playerId ?? player.name}
                                closable={Object.keys(item.winner).length === 0 && true}
                                onClose={() => deletePlayer({ playerId: player.playerId }, item.gameId)}
                              >
                                {player.name}
                              </Tag>
                            ))}
                          </div>
                          <Button
                            type="primary"
                            size="small"
                            disabled={isAddGameSubmitting || Object.keys(item.winner).length > 0 }
                            onClick={() => handleAddPlayer(item)}
                          >
                            <UsergroupAddOutlined />
                            Add player or team name
                          </Button>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={8} offset={8}>
                        <Button
                        block
                        type='default'
                        disabled={(item.players.length > item.questions.length) ||
                          (item.players.length === 0 && item.questions.length === 0) ||
                          (item.questions.length % item.players.length !== 0)}
                          onClick={() => handleOnClick(item)}
                        >
                          <PlayCircleOutlined />
                          <span>{`Play ${item.title}`}</span>
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={{ span: 4, offset: 14 }} md={{ span: 4, offset: 18 }} lg={{ span: 4, offset: 20 }}>
                        <Button type='link' htmlType="submit" disabled={isDeleteGameSubmitting}  onClick={() => { setGameId(item.gameId); handleOpenDeleteGameModal(); }}>
                          <DeleteOutlined />
                          Remove game
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                </Badge.Ribbon>
              </List.Item>
            )}
          />
        </Col>
      </Row>
      {/* Game modal */}
      <Modal title="Enter game title" open={isAddGameModalVisible} onCancel={handleOpenAddGameModal} footer={false}>
        <Form
          layout="vertical"
          ref={formRef}
          onFinish={onAddGameFinish}
          onFinishFailed={onAddGameFinishFailed}
        >
          <Form.Item label="Game" name="title" rules={[{ required: true, message: 'Please input game title!' }]}>
            <Input />
          </Form.Item>
          <Form.Item>
            <Space wrap>
              <Button type="primary" htmlType="submit" disabled={isAddGameSubmitting}>
                <PlusCircleOutlined />
                Add
              </Button>
              <Button type="text" disabled={isAddGameSubmitting} onClick={handleOpenAddGameModal}>
                <CloseOutlined />
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Delete game" open={isDeleteGameModalVisible} onCancel={handleOpenDeleteGameModal}  footer={false}>
        <Row>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <br />
            <Alert
              title="Are you sure you want to delte game"
              type="error"
              showIcon
            />
            <br />
            <Space wrap>
              <Button type="primary" htmlType="submit" disabled={isDeleteGameSubmitting} onClick={onDeleteGameHandler}>
                <DeleteOutlined />
                Delete
              </Button>
              <Button type="text" disabled={isDeleteGameSubmitting} onClick={handleOpenDeleteGameModal}>
                <CloseOutlined />
                Cancel
              </Button>
            </Space>
          </Col>
        </Row>
      </Modal>
       
      {/* Player modal */}
      <Modal
        title="Enter player"
        open={isAddPlayerModalVisible}
        onOk={handleOpenAddPlayerModal}
        onCancel={handleOpenAddPlayerModal}
        footer={false}
      >
        {games.find(game => game.gameId === currentGameId)?.players.map((player) => (
          <Tag
            key={player?.playerId ?? player.name}
            closable={true}
            onClose={() => deletePlayer({ playerId: player.playerId }, currentGameId)}
          >
            {player.name}
          </Tag>
        ))}
        <Form
          layout="vertical"
          ref={formRef}
          onFinish={(value) => onPlayerFinish(value, currentGameId)}
          onFinishFailed={onPlayerFinishFailed}
          style={{ marginTop: 20 }}
        >
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please write player name!' }]}>
            <Input />
          </Form.Item>

          <Form.Item>
            <Space wrap>
              <Button type="primary" htmlType="submit" disabled={isAddGameSubmitting}>
                <PlusCircleOutlined />
                Add
              </Button>
              <Button type="text" disabled={isAddGameSubmitting} onClick={handleOpenAddPlayerModal}>
                <CloseOutlined />
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Question modal */}
      <Modal
        title="Enter question"
        open={isAddQuestionModalVisible}
        onOk={handleOpenAddQuestionModal}
        onCancel={handleOpenAddQuestionModal}
        footer={false}
      >
        {games.find(game => game.gameId === currentGameId)?.questions.map((question) => (
          <Tag
            key={question?.questionId ?? question.question}
            closable={true}
            onClose={() => deleteQuestion({ questionId: question.questionId }, currentGameId)}
          >
            {question.question}
          </Tag>
        ))}
        <Form
          layout="vertical"
          ref={formRef}
          onFinish={(value) => onQuestionFinish(value, currentGameId)}
          onFinishFailed={onQuestionFinishFailed}
          style={{ marginTop: 20 }}
        >
          <Form.Item label="Question" name="question" rules={[{ required: true, message: 'Please write question!' }]}>
            <Input />
          </Form.Item>
          <Form.Item>
            <Space wrap>
              <Button type="primary" htmlType="submit" disabled={isAddGameSubmitting}>
                <PlusCircleOutlined />
                Add
              </Button>
              <Button type="text" disabled={isAddGameSubmitting} onClick={handleOpenAddQuestionModal}>
                <CloseOutlined />
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function mapStateToProps(state: any) {
  return {
    games: state.games,
  };
}

export default connect<StateProps, DispatchProps>(mapStateToProps, { getGames , createGame, deleteGame, addPlayer, addQuestion, deletePlayer, deleteQuestion } as any)(
  Games
);
